import { CurrencyIsoCode } from "@/enums";

/**
 * 유로존(euro area) = 유로를 **법정통화로 채택한 EU 회원국** 21개국. 2026-01-01 불가리아 합류 반영.
 *
 * 기획 확정(2026-08-07): Fixed price 대상은 **US / EU(유로존) / GB** 뿐이고 그 외는 전부 Dynamic.
 * - **유로 미사용 EU 회원국 6개국은 제외**(체코 CZK / 덴마크 DKK / 헝가리 HUF / 폴란드 PLN /
 *   루마니아 RON / 스웨덴 SEK) → Dynamic. 자국 통화라 EUR 고정가를 적용할 수 없다.
 * - **EU 밖에서 유로를 쓰는 지역도 제외**(모나코·산마리노·안도라·바티칸=통화협정,
 *   몬테네그로·코소보=일방 사용). 유로존 회원국이 아니므로 Dynamic.
 *
 * 이 목록은 **표시 통화(convertCountryCodeToCurrency)와 Fixed 판정(isFixedPriceCountry)의 단일 출처**다.
 * 둘이 어긋나면 #4975 과다청구가 재발한다(USD 숫자를 EUR 로 청구) — 반드시 같이 움직일 것.
 */
export const EUROZONE_COUNTRY_CODES: readonly string[] = [
  "AT", // Austria
  "BE", // Belgium
  "BG", // Bulgaria (2026-01-01 유로 도입)
  "HR", // Croatia
  "CY", // Cyprus
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "GR", // Greece
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "NL", // Netherlands
  "PT", // Portugal
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain
];

type CountryCallingCodes = {
  [key: string]: number;
};

const countryToCallingCode: CountryCallingCodes = {
  AF: 93, // Afghanistan
  AL: 355, // Albania
  DZ: 213, // Algeria
  AS: 1684, // American Samoa
  AD: 376, // Andorra
  AO: 244, // Angola
  AI: 1264, // Anguilla
  AG: 1268, // Antigua and Barbuda
  AR: 54, // Argentina
  AM: 374, // Armenia
  AW: 297, // Aruba
  AU: 61, // Australia
  AT: 43, // Austria
  AZ: 994, // Azerbaijan
  BS: 1242, // Bahamas
  BH: 973, // Bahrain
  BD: 880, // Bangladesh
  BB: 1246, // Barbados
  BY: 375, // Belarus
  BE: 32, // Belgium
  BZ: 501, // Belize
  BJ: 229, // Benin
  BM: 1441, // Bermuda
  BT: 975, // Bhutan
  BO: 591, // Bolivia
  BA: 387, // Bosnia and Herzegovina
  BW: 267, // Botswana
  BR: 55, // Brazil
  IO: 246, // British Indian Ocean Territory
  VG: 1284, // British Virgin Islands
  BN: 673, // Brunei
  BG: 359, // Bulgaria
  BF: 226, // Burkina Faso
  BI: 257, // Burundi
  KH: 855, // Cambodia
  CM: 237, // Cameroon
  CA: 1, // Canada
  CV: 238, // Cape Verde
  KY: 1345, // Cayman Islands
  CF: 236, // Central African Republic
  TD: 235, // Chad
  CL: 56, // Chile
  CN: 86, // China
  CX: 61, // Christmas Island
  CC: 61, // Cocos Islands
  CO: 57, // Colombia
  KM: 269, // Comoros
  CK: 682, // Cook Islands
  CR: 506, // Costa Rica
  HR: 385, // Croatia
  CU: 53, // Cuba
  CW: 599, // Curacao
  CY: 357, // Cyprus
  CZ: 420, // Czech Republic
  CD: 243, // Democratic Republic of the Congo
  DK: 45, // Denmark
  DJ: 253, // Djibouti
  DM: 1767, // Dominica
  DO: 1809, // Dominican Republic
  TL: 670, // East Timor
  EC: 593, // Ecuador
  EG: 20, // Egypt
  SV: 503, // El Salvador
  GQ: 240, // Equatorial Guinea
  ER: 291, // Eritrea
  EE: 372, // Estonia
  ET: 251, // Ethiopia
  FK: 500, // Falkland Islands
  FO: 298, // Faroe Islands
  FJ: 679, // Fiji
  FI: 358, // Finland
  FR: 33, // France
  PF: 689, // French Polynesia
  GA: 241, // Gabon
  GM: 220, // Gambia
  GE: 995, // Georgia
  DE: 49, // Germany
  GH: 233, // Ghana
  GI: 350, // Gibraltar
  GR: 30, // Greece
  GL: 299, // Greenland
  GD: 1473, // Grenada
  GU: 1671, // Guam
  GT: 502, // Guatemala
  GG: 44, // Guernsey
  GN: 224, // Guinea
  GW: 245, // Guinea-Bissau
  GY: 592, // Guyana
  HT: 509, // Haiti
  HN: 504, // Honduras
  HK: 852, // Hong Kong
  HU: 36, // Hungary
  IS: 354, // Iceland
  IN: 91, // India
  ID: 62, // Indonesia
  IR: 98, // Iran
  IQ: 964, // Iraq
  IE: 353, // Ireland
  IM: 44, // Isle of Man
  IL: 972, // Israel
  IT: 39, // Italy
  CI: 225, // Ivory Coast
  JM: 1876, // Jamaica
  JP: 81, // Japan
  JE: 44, // Jersey
  JO: 962, // Jordan
  KZ: 7, // Kazakhstan
  KE: 254, // Kenya
  KI: 686, // Kiribati
  XK: 383, // Kosovo
  KW: 965, // Kuwait
  KG: 996, // Kyrgyzstan
  LA: 856, // Laos
  LV: 371, // Latvia
  LB: 961, // Lebanon
  LS: 266, // Lesotho
  LR: 231, // Liberia
  LY: 218, // Libya
  LI: 423, // Liechtenstein
  LT: 370, // Lithuania
  LU: 352, // Luxembourg
  MO: 853, // Macau
  MK: 389, // Macedonia
  MG: 261, // Madagascar
  MW: 265, // Malawi
  MY: 60, // Malaysia
  MV: 960, // Maldives
  ML: 223, // Mali
  MT: 356, // Malta
  MH: 692, // Marshall Islands
  MR: 222, // Mauritania
  MU: 230, // Mauritius
  YT: 262, // Mayotte
  MX: 52, // Mexico
  FM: 691, // Micronesia
  MD: 373, // Moldova
  MC: 377, // Monaco
  MN: 976, // Mongolia
  ME: 382, // Montenegro
  MS: 1664, // Montserrat
  MA: 212, // Morocco
  MZ: 258, // Mozambique
  MM: 95, // Myanmar
  NA: 264, // Namibia
  NR: 674, // Nauru
  NP: 977, // Nepal
  NL: 31, // Netherlands
  NC: 687, // New Caledonia
  NZ: 64, // New Zealand
  NI: 505, // Nicaragua
  NE: 227, // Niger
  NG: 234, // Nigeria
  NU: 683, // Niue
  KP: 850, // North Korea
  MP: 1670, // Northern Mariana Islands
  NO: 47, // Norway
  OM: 968, // Oman
  PK: 92, // Pakistan
  PW: 680, // Palau
  PS: 970, // Palestine
  PA: 507, // Panama
  PG: 675, // Papua New Guinea
  PY: 595, // Paraguay
  PE: 51, // Peru
  PH: 63, // Philippines
  PN: 64, // Pitcairn
  PL: 48, // Poland
  PT: 351, // Portugal
  PR: 1, // Puerto Rico
  QA: 974, // Qatar
  CG: 242, // Republic of the Congo
  RE: 262, // Reunion
  RO: 40, // Romania
  RU: 7, // Russia
  RW: 250, // Rwanda
  BL: 590, // Saint Barthelemy
  SH: 290, // Saint Helena
  KN: 1869, // Saint Kitts and Nevis
  LC: 1758, // Saint Lucia
  MF: 590, // Saint Martin
  PM: 508, // Saint Pierre and Miquelon
  VC: 1784, // Saint Vincent and the Grenadines
  WS: 685, // Samoa
  SM: 378, // San Marino
  ST: 239, // Sao Tome and Principe
  SA: 966, // Saudi Arabia
  SN: 221, // Senegal
  RS: 381, // Serbia
  SC: 248, // Seychelles
  SL: 232, // Sierra Leone
  SG: 65, // Singapore
  SX: 1721, // Sint Maarten
  SK: 421, // Slovakia
  SI: 386, // Slovenia
  SB: 677, // Solomon Islands
  SO: 252, // Somalia
  ZA: 27, // South Africa
  KR: 82, // South Korea
  SS: 211, // South Sudan
  ES: 34, // Spain
  LK: 94, // Sri Lanka
  SD: 249, // Sudan
  SR: 597, // Suriname
  SJ: 47, // Svalbard and Jan Mayen
  SZ: 268, // Swaziland
  SE: 46, // Sweden
  CH: 41, // Switzerland
  SY: 963, // Syria
  TW: 886, // Taiwan
  TJ: 992, // Tajikistan
  TZ: 255, // Tanzania
  TH: 66, // Thailand
  TG: 228, // Togo
  TK: 690, // Tokelau
  TO: 676, // Tonga
  TT: 1868, // Trinidad and Tobago
  TN: 216, // Tunisia
  TR: 90, // Turkey
  TM: 993, // Turkmenistan
  TC: 1649, // Turks and Caicos Islands
  TV: 688, // Tuvalu
  VI: 1340, // U.S. Virgin Islands
  UG: 256, // Uganda
  UA: 380, // Ukraine
  AE: 971, // United Arab Emirates
  GB: 44, // United Kingdom
  US: 1, // United States
  UY: 598, // Uruguay
  UZ: 998, // Uzbekistan
  VU: 678, // Vanuatu
  VA: 379, // Vatican
  VE: 58, // Venezuela
  VN: 84, // Vietnam
  WF: 681, // Wallis and Futuna
  EH: 212, // Western Sahara
  YE: 967, // Yemen
  ZM: 260, // Zambia
  ZW: 263, // Zimbabwe
};

const countryNames: { [key: string]: string } = {
  AF: "Afghanistan",
  AL: "Albania",
  DZ: "Algeria",
  AS: "American Samoa",
  AD: "Andorra",
  AO: "Angola",
  AI: "Anguilla",
  AG: "Antigua and Barbuda",
  AR: "Argentina",
  AM: "Armenia",
  AW: "Aruba",
  AU: "Australia",
  AT: "Austria",
  AZ: "Azerbaijan",
  BS: "Bahamas",
  BH: "Bahrain",
  BD: "Bangladesh",
  BB: "Barbados",
  BY: "Belarus",
  BE: "Belgium",
  BZ: "Belize",
  BJ: "Benin",
  BM: "Bermuda",
  BT: "Bhutan",
  BO: "Bolivia",
  BA: "Bosnia and Herzegovina",
  BW: "Botswana",
  BR: "Brazil",
  IO: "British Indian Ocean Territory",
  VG: "British Virgin Islands",
  BN: "Brunei",
  BG: "Bulgaria",
  BF: "Burkina Faso",
  BI: "Burundi",
  KH: "Cambodia",
  CM: "Cameroon",
  CA: "Canada",
  CV: "Cape Verde",
  KY: "Cayman Islands",
  CF: "Central African Republic",
  TD: "Chad",
  CL: "Chile",
  CN: "China",
  CX: "Christmas Island",
  CC: "Cocos Islands",
  CO: "Colombia",
  KM: "Comoros",
  CK: "Cook Islands",
  CR: "Costa Rica",
  HR: "Croatia",
  CU: "Cuba",
  CW: "Curacao",
  CY: "Cyprus",
  CZ: "Czech Republic",
  CD: "Democratic Republic of the Congo",
  DK: "Denmark",
  DJ: "Djibouti",
  DM: "Dominica",
  DO: "Dominican Republic",
  TL: "East Timor",
  EC: "Ecuador",
  EG: "Egypt",
  SV: "El Salvador",
  GQ: "Equatorial Guinea",
  ER: "Eritrea",
  EE: "Estonia",
  ET: "Ethiopia",
  FK: "Falkland Islands",
  FO: "Faroe Islands",
  FJ: "Fiji",
  FI: "Finland",
  FR: "France",
  PF: "French Polynesia",
  GA: "Gabon",
  GM: "Gambia",
  GE: "Georgia",
  DE: "Germany",
  GH: "Ghana",
  GI: "Gibraltar",
  GR: "Greece",
  GL: "Greenland",
  GD: "Grenada",
  GU: "Guam",
  GT: "Guatemala",
  GG: "Guernsey",
  GN: "Guinea",
  GW: "Guinea-Bissau",
  GY: "Guyana",
  HT: "Haiti",
  HN: "Honduras",
  HK: "Hong Kong",
  HU: "Hungary",
  IS: "Iceland",
  IN: "India",
  ID: "Indonesia",
  IR: "Iran",
  IQ: "Iraq",
  IE: "Ireland",
  IM: "Isle of Man",
  IL: "Israel",
  IT: "Italy",
  CI: "Ivory Coast",
  JM: "Jamaica",
  JP: "Japan",
  JE: "Jersey",
  JO: "Jordan",
  KZ: "Kazakhstan",
  KE: "Kenya",
  KI: "Kiribati",
  XK: "Kosovo",
  KW: "Kuwait",
  KG: "Kyrgyzstan",
  LA: "Laos",
  LV: "Latvia",
  LB: "Lebanon",
  LS: "Lesotho",
  LR: "Liberia",
  LY: "Libya",
  LI: "Liechtenstein",
  LT: "Lithuania",
  LU: "Luxembourg",
  MO: "Macau",
  MK: "Macedonia",
  MG: "Madagascar",
  MW: "Malawi",
  MY: "Malaysia",
  MV: "Maldives",
  ML: "Mali",
  MT: "Malta",
  MH: "Marshall Islands",
  MR: "Mauritania",
  MU: "Mauritius",
  YT: "Mayotte",
  MX: "Mexico",
  FM: "Micronesia",
  MD: "Moldova",
  MC: "Monaco",
  MN: "Mongolia",
  ME: "Montenegro",
  MS: "Montserrat",
  MA: "Morocco",
  MZ: "Mozambique",
  MM: "Myanmar",
  NA: "Namibia",
  NR: "Nauru",
  NP: "Nepal",
  NL: "Netherlands",
  NC: "New Caledonia",
  NZ: "New Zealand",
  NI: "Nicaragua",
  NE: "Niger",
  NG: "Nigeria",
  NU: "Niue",
  KP: "North Korea",
  MP: "Northern Mariana Islands",
  NO: "Norway",
  OM: "Oman",
  PK: "Pakistan",
  PW: "Palau",
  PS: "Palestine",
  PA: "Panama",
  PG: "Papua New Guinea",
  PY: "Paraguay",
  PE: "Peru",
  PH: "Philippines",
  PN: "Pitcairn",
  PL: "Poland",
  PT: "Portugal",
  PR: "Puerto Rico",
  QA: "Qatar",
  CG: "Republic of the Congo",
  RE: "Reunion",
  RO: "Romania",
  RU: "Russia",
  RW: "Rwanda",
  BL: "Saint Barthelemy",
  SH: "Saint Helena",
  KN: "Saint Kitts and Nevis",
  LC: "Saint Lucia",
  MF: "Saint Martin",
  PM: "Saint Pierre and Miquelon",
  VC: "Saint Vincent and the Grenadines",
  WS: "Samoa",
  SM: "San Marino",
  ST: "Sao Tome and Principe",
  SA: "Saudi Arabia",
  SN: "Senegal",
  RS: "Serbia",
  SC: "Seychelles",
  SL: "Sierra Leone",
  SG: "Singapore",
  SX: "Sint Maarten",
  SK: "Slovakia",
  SI: "Slovenia",
  SB: "Solomon Islands",
  SO: "Somalia",
  ZA: "South Africa",
  KR: "South Korea",
  SS: "South Sudan",
  ES: "Spain",
  LK: "Sri Lanka",
  SD: "Sudan",
  SR: "Suriname",
  SJ: "Svalbard and Jan Mayen",
  SZ: "Swaziland",
  SE: "Sweden",
  CH: "Switzerland",
  SY: "Syria",
  TW: "Taiwan",
  TJ: "Tajikistan",
  TZ: "Tanzania",
  TH: "Thailand",
  TG: "Togo",
  TK: "Tokelau",
  TO: "Tonga",
  TT: "Trinidad and Tobago",
  TN: "Tunisia",
  TR: "Turkey",
  TM: "Turkmenistan",
  TC: "Turks and Caicos Islands",
  TV: "Tuvalu",
  VI: "U.S. Virgin Islands",
  UG: "Uganda",
  UA: "Ukraine",
  AE: "United Arab Emirates",
  GB: "United Kingdom",
  US: "United States",
  UY: "Uruguay",
  UZ: "Uzbekistan",
  VU: "Vanuatu",
  VA: "Vatican",
  VE: "Venezuela",
  VN: "Vietnam",
  WF: "Wallis and Futuna",
  EH: "Western Sahara",
  YE: "Yemen",
  ZM: "Zambia",
  ZW: "Zimbabwe",
};

export default class CountryUtils {
  public static getCallingCode = (countryCode: string): number => {
    const upperCountryCode = countryCode.toUpperCase();
    return countryToCallingCode[upperCountryCode] ?? 1;
  };
  public static getCountryName = (countryCode: string): string => {
    const upperCountryCode = countryCode.toUpperCase();
    return countryNames[upperCountryCode] ?? countryCode;
  };

  public static convertCountryCodeToFlagEmoji(countryCode: string) {
    if (!countryCode || countryCode.length !== 2) {
      throw new Error(
        "올바른 ISO 3166-1 알파-2 형식의 국가 코드를 입력하세요.",
      );
    }

    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);

    return String.fromCodePoint(...codePoints);
  }

  /**
   * 국가 → 표시/청구 통화. 6타입 가격 모델(docs/pricing-tiers.md).
   *
   * ⚠️ 가격 조회는 `prices.find(p => p.currency === currency)` **정확 매칭**이라,
   * 여기서 고른 통화와 `getCountryAwarePrices` 가 돌려주는 가격의 통화가 **반드시 일치**해야 한다.
   * 어긋나면 가격이 `null` 이 되어 표시가 사라지거나(비-GE), USD 숫자가 EUR 로 청구된다(#4975).
   *
   * ★`euGbEnabled` (기본 **false** = 기존 4타입 동작):
   * EUR/GBP 는 **Global-E 체크아웃 전용 통화**다. 자체 PG(PayPal/Eximbay)는 `draft.currency` 를
   * 그대로 PG 로 넘길 뿐이라, GE 를 안 거치는데 € 로 표시하면 **표시 통화와 실제 정산 통화가
   * 갈릴 수 있다**(PG 머천트 계정의 EUR 정산 지원 여부는 코드로 확인 불가). 그래서 EU/GB 전환은
   * `globalEEnabled` flag 가 확정 true 일 때만 켠다 — 클라이언트 진입점은 `useCurrency()` 하나.
   *
   * 기본값을 false 로 둔 이유: 새 호출처가 옵션을 잊어도 **기존 USD 동작으로 닫히도록**
   * (fail-safe). 반대로 두면 감사 안 된 경로가 조용히 EUR 로 새어나간다.
   */
  public static convertCountryCodeToCurrency(
    countryCode: string,
    { euGbEnabled = false }: { euGbEnabled?: boolean } = {},
  ) {
    const cc = (countryCode ?? "").trim().toUpperCase();
    if (cc === "KR") {
      return CurrencyIsoCode.KRW;
    } else if (cc === "JP") {
      return CurrencyIsoCode.JPY;
    } else if (euGbEnabled && cc === "GB") {
      return CurrencyIsoCode.GBP;
    } else if (euGbEnabled && EUROZONE_COUNTRY_CODES.includes(cc)) {
      return CurrencyIsoCode.EUR;
    } else {
      // US 포함 그 외 전부 USD (US=usPrice final, 나머지=기준가 USD → GE 가 Dynamic 변환).
      return CurrencyIsoCode.USD;
    }
  }

  public static isKR(countryCode: string | null | undefined): boolean {
    return (countryCode ?? "").trim().toUpperCase() === "KR";
  }
}
